/**
 * createUseContactForm — hook factory for marketing contact-forms.
 *
 * Returns a React hook configured with the consumer's API client, the
 * canonical Joi schema (or any object exposing `validate(values)`), and
 * the i18n translation function. The hook implements the canonical
 * submission state machine:
 *
 *   idle → submitting → (success | error)
 *
 * + handles:
 *   - Honeypot bypass (a filled `website` field short-circuits with the
 *     `honeypot` outcome client-side so the form never POSTs)
 *   - Client-side validation via the supplied validator
 *   - 10 outcome → user-visible message mapping (i18n key
 *     `contact.errors.<outcome>` resolved via the supplied `t`)
 *   - onOutcome callback for analytics emission
 *   - Reset action that returns the form to idle
 *
 * The hook is unaware of UI specifics — consumers render their own
 * fields and bind to `fields`, `errors`, `state`, `outcome`, `submit`,
 * `reset`. The companion `<ContactForm>` component is a thin
 * fields-driven renderer that consumes the hook.
 *
 * @module @puffin-core/marketing-shell/contact/createUseContactForm
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useRateLimit, resolveRateLimitPolicy } from '@puffin-core/form-security';
import { isContactOutcome, isRateLimitedOutcome, isSuccessOutcome } from './contactOutcomes.js';

const STATE_IDLE = 'idle';

// A server-side limiter's outcome (`isRateLimitedOutcome`, the ONE predicate in `contactOutcomes`) locks the
// client throttle for the server's `retryAfter`, so the ONE `<RateLimitNotice>` countdown speaks for both sides.

/**
 * Lock the client throttle for the server's `retryAfter` (seconds, from the 429 envelope), rounded
 * UP to whole seconds so the client never unlocks before the server would accept again. An absent
 * or non-positive value locks nothing beyond the attempt already counted.
 *
 * @param {{lockFor: (ms: number) => void}} rateLimit - the `useRateLimit()` controls
 * @param {*} retryAfter - the envelope's `retryAfter`, seconds
 * @returns {void}
 */
function lockForRetryAfter(rateLimit, retryAfter) {
  const seconds = Number(retryAfter);
  if (!Number.isFinite(seconds) || seconds <= 0) return;
  rateLimit.lockFor(Math.ceil(seconds) * 1000);
}
/** The refusal of a validator error result that is not the Joi ValidationError shape. */
const INVALID_VALIDATOR_RESULT =
  'createUseContactForm: a validator error must carry `details`, a list of { path, message } (the Joi ValidationError shape)';

/**
 * @param {*} details - A validator error's `details`.
 * @returns {boolean} Whether it is a list whose every entry carries a `path` list (Joi's shape; an empty `path` is an
 *   object-level error that names no field).
 */
function isValidationDetailList(details) {
  return Array.isArray(details) && details.every((detail) => Array.isArray(detail?.path));
}

const STATE_SUBMITTING = 'submitting';
const STATE_SUCCESS = 'success';
const STATE_ERROR = 'error';

/**
 * @typedef {Object} ContactValidator
 * @property {(values: Object) => ({error?: Object, value: Object}|Promise<{error?: Object, value: Object}>)} validate
 *   The Joi schema object meets this contract via its `.validate()` method. The result may be a
 *   promise: a validator that needs a heavy parser (the phone field's libphonenumber) loads it at
 *   submit time instead of shipping it in the page's eager bundle (PERF-FECHUNK-001).
 */

/**
 * @typedef {Object} ContactApiClient
 * @property {(values: Object, opts?: Object) => Promise<{success, data?, error?, outcome?, submissionId?}>} submit
 *   Submits the form. Returns the canonical response envelope (per
 *   @puffin-core/response-helpers). When the backend returns a non-2xx
 *   we still expect the envelope to carry { success: false, outcome }.
 */

/**
 * @typedef {Object} CreateUseContactFormConfig
 * @property {ContactValidator} validator
 * @property {ContactApiClient} apiClient
 * @property {(key: string, opts?: Object) => string} t
 *   i18n translation function. Keys consumed: `contact.errors.<outcome>`,
 *   `contact.errors.network`, `contact.errors.unknown`.
 * @property {(ctx: {outcome, submissionId?, durationMs, retryAfter?}) => void} [onOutcome]
 * @property {() => void} [onFirstInteract]
 *   Fires once the first time `setField` is called for a given hook
 *   instance. Used by ContactPage callers to emit the
 *   `marketing.<appKey>.contact_form_started` conversion event without
 *   coupling the canonical hook to AnalyticsProvider.
 * @property {Object} [defaultValues={}]
 * @property {string} [i18nNamespacePrefix='contact']
 *   Override the i18n key prefix when the consumer's bundle uses a
 *   different namespace. Defaults to 'contact'.
 * @property {{maxAttempts?: number, windowMs?: number, lockoutMs?: number}} [rateLimit]
 *   The client throttle's policy (DTOWNRETAIL-SITE-001 U6, I-13), form-security's default — five
 *   submissions a minute — when absent. The hook owns the ONE client throttle (`useRateLimit`):
 *   every submission counts, a submit while locked is refused and changes no form state (the
 *   `<RateLimitNotice>` the form renders IS the refusal, Q-UMB-21), and a 429 locks for the server's
 *   `retryAfter`. The honeypot is judged first, so a bot never spends the budget. A bad policy
 *   throws here, at factory time.
 */

/**
 * Build the `useContactForm` hook with the consumer's wiring baked in.
 *
 * @param {CreateUseContactFormConfig} config
 * @returns {() => ContactFormApi}
 */
export default function createUseContactForm(config) {
  validateConfig(config);
  const {
    validator,
    apiClient,
    t,
    onOutcome,
    onFirstInteract,
    defaultValues = {},
    i18nNamespacePrefix = 'contact',
    rateLimit: rateLimitOptions = {},
  } = config;
  // Resolved ONCE, here: a policy that is not positive integers is a wiring bug and fails the
  // factory, never the first render.
  const rateLimitPolicy = resolveRateLimitPolicy(rateLimitOptions);

  function useContactForm() {
    const [fields, setFields] = useState({ ...defaultValues });
    const [errors, setErrors] = useState({});
    const [state, setState] = useState(STATE_IDLE);
    const [outcome, setOutcome] = useState(null);
    const [submissionId, setSubmissionId] = useState(null);
    const [message, setMessage] = useState(null);
    const inFlightRef = useRef(false);
    const firstInteractRef = useRef(false);
    const rateLimit = useRateLimit(rateLimitPolicy);
    const { checkLimit, lockFor } = rateLimit;

    const setField = useCallback((name, value) => {
      if (!firstInteractRef.current) {
        firstInteractRef.current = true;
        if (typeof onFirstInteract === 'function') {
          onFirstInteract();
        }
      }
      setFields((prev) => ({ ...prev, [name]: value }));
      // Clearing the error inline gives the user immediate feedback when
      // they correct a field.
      setErrors((prev) => {
        if (!prev[name]) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }, []);

    const reset = useCallback(() => {
      setFields({ ...defaultValues });
      setErrors({});
      setState(STATE_IDLE);
      setOutcome(null);
      setSubmissionId(null);
      setMessage(null);
      inFlightRef.current = false;
      firstInteractRef.current = false;
    }, []);

    const submit = useCallback(
      async (overrides = {}) => {
        if (inFlightRef.current) return;
        const values = { ...fields, ...overrides };

        // Honeypot short-circuit. We never POST when the bot trap is filled.
        if (values.website && String(values.website).trim().length > 0) {
          const result = {
            outcome: 'honeypot',
            success: false,
            message: t(`${i18nNamespacePrefix}.errors.honeypot`),
          };
          setState(STATE_ERROR);
          setOutcome('honeypot');
          setMessage(result.message);
          if (typeof onOutcome === 'function') {
            onOutcome({ outcome: 'honeypot', durationMs: 0 });
          }
          return;
        }

        // The client throttle (I-13): every submission counts, and a submit while locked is refused
        // here, changing no form state — `<RateLimitNotice>` is the refusal the person sees.
        if (!checkLimit()) return;

        // The form is SUBMITTING from here, validation included: a validator may be async (the
        // phone field's parser loads at submit time), and the in-flight latch + the submitting
        // state keep a second submit a no-op while it runs. Both come back if validation refuses
        // or throws.
        inFlightRef.current = true;
        setState(STATE_SUBMITTING);
        setOutcome(null);
        setMessage(null);
        let v;
        try {
          v = await validator.validate(values, { abortEarly: false });
        } catch (err) {
          inFlightRef.current = false;
          setState(STATE_IDLE);
          throw err;
        }
        if (v && v.error) {
          inFlightRef.current = false;
          // The Joi ValidationError shape is the contract (every app's validator returns it). A malformed result is
          // a defect, refused by name: read as "no field is wrong", it showed a failure that pointed at nothing.
          if (!isValidationDetailList(v.error.details)) {
            setState(STATE_IDLE);
            throw new TypeError(INVALID_VALIDATOR_RESULT);
          }
          const fieldErrors = {};
          for (const detail of v.error.details) {
            const [key] = detail.path;
            if (key && !fieldErrors[key]) {
              fieldErrors[key] = detail.message;
            }
          }
          setErrors(fieldErrors);
          setState(STATE_ERROR);
          setOutcome('validation_fail');
          setMessage(t(`${i18nNamespacePrefix}.errors.validation_fail`));
          if (typeof onOutcome === 'function') {
            onOutcome({ outcome: 'validation_fail', durationMs: 0 });
          }
          return;
        }

        // POST.
        const startedAt = Date.now();

        try {
          const result = await apiClient.submit(v && v.value ? v.value : values);
          const durationMs = Date.now() - startedAt;
          const resolvedOutcome = isContactOutcome(result?.outcome)
            ? result.outcome
            : result?.success
              ? 'success'
              : 'persist_fail';
          const resolvedMessage = t(`${i18nNamespacePrefix}.errors.${resolvedOutcome}`);
          setOutcome(resolvedOutcome);
          setMessage(resolvedMessage);
          setSubmissionId(result?.data?.submissionId || result?.submissionId || null);
          setState(isSuccessOutcome(resolvedOutcome) ? STATE_SUCCESS : STATE_ERROR);
          // A server refusal as rate-limited locks the client for as long as the server asked.
          if (isRateLimitedOutcome(resolvedOutcome)) {
            lockForRetryAfter({ lockFor }, result?.retryAfter);
          }
          if (typeof onOutcome === 'function') {
            onOutcome({
              outcome: resolvedOutcome,
              submissionId: result?.data?.submissionId || result?.submissionId || null,
              durationMs,
              retryAfter: result?.retryAfter,
            });
          }
        } catch (err) {
          const durationMs = Date.now() - startedAt;
          setState(STATE_ERROR);
          setOutcome('persist_fail');
          setMessage(t(`${i18nNamespacePrefix}.errors.persist_fail`));
          if (typeof onOutcome === 'function') {
            onOutcome({
              outcome: 'persist_fail',
              durationMs,
              error: err,
            });
          }
        } finally {
          inFlightRef.current = false;
        }
      },
      // The useCallback re-binds when `fields` changes; the throttle's two controls are stable
      // callbacks (not the ticking state object), and the other closure-captured values
      // (validator/apiClient/t/onOutcome) are stable factory-level injections.
      [fields, checkLimit, lockFor],
    );

    const api = useMemo(
      () => ({
        fields,
        errors,
        state,
        outcome,
        submissionId,
        message,
        setField,
        submit,
        reset,
        isSubmitting: state === STATE_SUBMITTING,
        isSuccess: state === STATE_SUCCESS,
        isError: state === STATE_ERROR,
        // The client throttle's state — what `<RateLimitNotice>` renders and the submit disables on.
        rateLimit,
        isThrottled: rateLimit.isLimited,
      }),
      [fields, errors, state, outcome, submissionId, message, setField, submit, reset, rateLimit],
    );

    return api;
  }

  return useContactForm;
}

function validateConfig(config) {
  if (!config || typeof config !== 'object') {
    throw new TypeError('createUseContactForm: config object is required');
  }
  if (!config.validator || typeof config.validator.validate !== 'function') {
    throw new TypeError(
      'createUseContactForm: config.validator.validate must be a function (Joi schema or compatible)',
    );
  }
  if (!config.apiClient || typeof config.apiClient.submit !== 'function') {
    throw new TypeError('createUseContactForm: config.apiClient.submit must be a function');
  }
  if (typeof config.t !== 'function') {
    throw new TypeError('createUseContactForm: config.t (i18n function) is required');
  }
}
